import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MessageSquare, MoveRight, UserCog } from "lucide-react";
import { EventEnrollmentItemResponseDTO } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventEnrollments/types";
import { EventWaitlistItemResponse } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventWaitlistItems/types";
import { WaitlistDetails } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";
import WaitlistTable from "@/modules/eventRegistration/features/EventWaitlist/components/WaitlistTable/WaitlistTable";
import { RegisteredUser } from "@/modules/eventRegistration/features/EventWaitlist/hooks/useWaitlist";

type RosterFilter = "all" | "registered" | "waitlisted";

interface RosterTabProps {
  registeredUsers: EventEnrollmentItemResponseDTO[];
  waitlist: EventWaitlistItemResponse[];
  waitlistDetails: WaitlistDetails | null;
  isSessionFull: boolean;
  formatDate: (dateString: string) => string;
  handleReplaceClick: (registration: RegisteredUser) => void;
  handleMoveToWaitlistClick: (registration: RegisteredUser) => void;
  handleRegisterClick: (user: EventWaitlistItemResponse) => void;
  handleRemoveClick: (user: EventWaitlistItemResponse) => void;
  onPositionChange: (waitlistId: number, newPosition: number) => Promise<void>;
  onMoveToTop: (waitlistId: number) => Promise<void>;
}

export default function RosterTab({
  registeredUsers,
  waitlist,
  waitlistDetails,
  isSessionFull,
  formatDate,
  handleReplaceClick,
  handleMoveToWaitlistClick,
  handleRegisterClick,
  handleRemoveClick,
  onPositionChange,
  onMoveToTop,
}: RosterTabProps) {
  const [rosterFilter, setRosterFilter] = useState<RosterFilter>("all");
  const isSessionFinalized = waitlistDetails?.isSessionFinalized;

  const showRegistered = rosterFilter === "all" || rosterFilter === "registered";
  const showWaitlisted = rosterFilter === "all" || rosterFilter === "waitlisted";

  const filterButtons: { value: RosterFilter; label: string }[] = [
    { value: "all", label: `All (${registeredUsers.length + waitlist.length})` },
    { value: "registered", label: `Registered (${registeredUsers.length})` },
    { value: "waitlisted", label: `Waitlisted (${waitlist.length})` },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {filterButtons.map((btn) => (
          <button
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
              rosterFilter === btn.value
                ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white"
                : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
            }`}
            key={btn.value}
            onClick={() => setRosterFilter(btn.value)}
            type="button"
          >
            {btn.label}
          </button>
        ))}
      </div>

      {showRegistered && (
        <div className={showWaitlisted && rosterFilter === "all" ? "mb-8" : ""}>
          {rosterFilter === "all" && (
            <h3 className="text-base font-normal text-gray-900 dark:text-white mb-3">
              Registered ({registeredUsers.length})
            </h3>
          )}
          {registeredUsers.length === 0 ? (
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">
              No users registered for this session.
            </div>
          ) : (
            <div className="border border-gray-200 dark:border-slate-700 rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Registration Date</TableHead>
                    {!isSessionFinalized && (
                      <TableHead className="text-right">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registeredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                        <div className="flex items-center gap-2">
                          {user.firstName} {user.lastName}
                          {user.specialRequests && (
                            <SpecialRequestsPopover text={user.specialRequests} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-300">{user.email}</TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-300">
                        {formatDate(user.enrolledAt || "")}
                      </TableCell>
                      {!isSessionFinalized && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => handleReplaceClick(user)}
                              size="sm"
                              variant="outline"
                            >
                              <UserCog className="h-4 w-4 mr-1" />
                              Replace
                            </Button>
                            <Button
                              onClick={() => handleMoveToWaitlistClick(user)}
                              size="sm"
                              variant="outline"
                            >
                              <MoveRight className="h-4 w-4 mr-1" />
                              Move to Waitlist
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {showWaitlisted && (
        <div>
          {rosterFilter === "all" && (
            <h3 className="text-base font-normal text-gray-900 dark:text-white mb-3">
              Waitlisted ({waitlist.length})
            </h3>
          )}
          {waitlist.length === 0 ? (
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">
              No users on the waitlist for this session.
            </div>
          ) : (
            <WaitlistTable
              formatDate={formatDate}
              handleRegisterClick={handleRegisterClick}
              handleRemoveClick={handleRemoveClick}
              isSessionFinalized={isSessionFinalized}
              isSessionFull={isSessionFull}
              onMoveToTop={onMoveToTop}
              onPositionChange={onPositionChange}
              waitlist={waitlist}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SpecialRequestsPopover({ text }: { text: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title="Special requests"
          type="button"
        >
          <MessageSquare className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" side="top">
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Special Requests
          </p>
          <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
            {text}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
